import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowDown,
  FaCalendarAlt,
  FaChartLine,
  FaChevronRight,
  FaClipboardCheck,
  FaDumbbell,
  FaMedal,
  FaPlayCircle,
  FaTrophy,
  FaUserCheck,
  FaUserFriends,
  FaUsers,
} from "react-icons/fa";
import { GiKimono } from "react-icons/gi";

const getFirstName = (user) => {
  if (!user) return "";

  return (
    user.prenom ||
    user.first_name ||
    user.firstname ||
    user.username ||
    ""
  );
};

const MODE_CONTENT = {
  visitor: {
    eyebrow: "Application web pour judokas et coachs",
    title: "Suivez votre saison et progressez sur les tatamis.",
    description:
      "Judo Coach Pro centralise les résultats, les compétitions, les grades, les révisions et le suivi coach dans un espace pensé pour le judo.",
    primaryLabel: "Créer mon compte gratuit",
    primaryPath: "/register",
    secondaryLabel: "Découvrir l’espace coach",
    secondaryPath: "/espace-coach",
  },
  judoka: {
    eyebrow: "Votre espace judoka",
    title: "Gardez le fil de votre saison, de vos résultats et de vos progrès.",
    description:
      "Retrouvez vos compétitions, vos résultats, vos passages de grade, vos statistiques, vos révisions techniques et vos exercices à réaliser.",
    primaryLabel: "Voir mes résultats",
    primaryPath: "/resultats",
    secondaryLabel: "Mes compétitions",
    secondaryPath: "/events-judoka",
  },
  coach: {
    eyebrow: "Votre espace coach",
    title: "Accompagnez vos judokas avec un suivi clair et régulier.",
    description:
      "Créez vos groupes, suivez les présences, les résultats, les grades, les statistiques et attribuez du travail physique ciblé.",
    primaryLabel: "Mes groupes coach",
    primaryPath: "/groups-coach",
    secondaryLabel: "Résultats des groupes",
    secondaryPath: "/group-results",
  },
};

const BACKGROUND_IMAGES = [
  {
    src: "/img/HeaderSlider_03.jpg",
    alt: "Judo compétition",
  },
  {
    src: "/img/HeaderSlider_05.webp",
    alt: "Judo technique",
  },
  {
    src: "/img/HeaderSlider_06.webp",
    alt: "Judo entraînement",
  },
  {
    src: "/img/HeaderSlider_02.jpg",
    alt: "Coach judo",
  },
];

const FEATURE_AUDIENCES = {
  judoka: {
    label: "Judoka",
    title: "Un espace pour progresser",
    description:
      "Tout ce qu’il faut pour suivre sa saison, réviser et visualiser ses progrès.",
    ctaLabel: "Voir mon espace judoka",
    visitorCtaLabel: "Créer mon compte judoka",
    path: "/profile-judoka",
    visitorPath: "/register",
    icon: GiKimono,
    cardGradient:
      "from-sky-600/95 via-cyan-600/90 to-blue-500/95",
    glowClass: "bg-sky-600/30",
    iconClass:
      "border-sky-100/30 bg-white/20 text-white",
    features: [
      {
        icon: FaCalendarAlt,
        title: "Compétitions",
        text: "Calendrier, événements et inscriptions.",
      },
      {
        icon: FaTrophy,
        title: "Résultats",
        text: "Palmarès, podiums et historique.",
      },
      {
        icon: FaMedal,
        title: "Progression",
        text: "Ceintures, grades et objectifs.",
      },
      {
        icon: GiKimono,
        title: "Révisions",
        text: "Techniques, katas, lexique et quizz.",
      },
      {
        icon: FaDumbbell,
        title: "Prépa physique",
        text: "Exercices attribués par le coach.",
      },
    ],
  },

  coach: {
    label: "Coach",
    title: "Un suivi coach structuré",
    description:
      "Des outils simples pour organiser les groupes et accompagner les judokas toute la saison.",
    ctaLabel: "Voir mon espace coach",
    visitorCtaLabel: "Découvrir l’espace coach",
    path: "/groups-coach",
    visitorPath: "/espace-coach",
    icon: FaUserFriends,
    cardGradient:
      "from-rose-800/95 via-red-600/90 to-orange-800/95",
    glowClass: "bg-rose-800/30",
    iconClass:
      "border-rose-100/30 bg-white/20 text-white",
    features: [
      {
        icon: FaUsers,
        title: "Groupes",
        text: "Création, invitations et suivi.",
      },
      {
        icon: FaUserCheck,
        title: "Présences",
        text: "Suivi des cours et assiduité.",
      },
      {
        icon: FaChartLine,
        title: "Résultats",
        text: "Suivi individuel et collectif.",
      },
      {
        icon: FaClipboardCheck,
        title: "Évaluations grades",
        text: "Préparer les passages de grade.",
      },
      {
        icon: FaDumbbell,
        title: "Prépa physique",
        text: "Exercices attribués et validés.",
      },
    ],
  },
};

function HomeHeroSection({ user, mode = "visitor" }) {
  const navigate = useNavigate();

  const [currentBackgroundIndex, setCurrentBackgroundIndex] = useState(0);
  const [selectedAudience, setSelectedAudience] = useState(
    mode === "coach" ? "coach" : "judoka",
  );

  const firstName = getFirstName(user);
  const content = MODE_CONTENT[mode] || MODE_CONTENT.visitor;
  const featureAudience =
    FEATURE_AUDIENCES[selectedAudience] || FEATURE_AUDIENCES.judoka;

  const AudienceIcon = featureAudience.icon;

  useEffect(() => {
    setSelectedAudience(mode === "coach" ? "coach" : "judoka");
  }, [mode]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBackgroundIndex((prevIndex) =>
        prevIndex === BACKGROUND_IMAGES.length - 1 ? 0 : prevIndex + 1,
      );
    }, 9000);

    return () => clearInterval(interval);
  }, []);

  const scrollToNextSection = () => {
    const nextSection = document.getElementById("agenda");

    if (nextSection) {
      window.scrollTo({
        top: nextSection.offsetTop - 78,
        behavior: "smooth",
      });
    }
  };

  const handleAudienceCta = () => {
    if (mode === "visitor") {
      navigate(featureAudience.visitorPath);
      return;
    }

    navigate(featureAudience.path);
  };

  const audienceCtaLabel =
    mode === "visitor"
      ? featureAudience.visitorCtaLabel
      : featureAudience.ctaLabel;

  return (
    <header className="relative min-h-[calc(100vh-78px)] overflow-hidden bg-slate-950 text-white">
      {BACKGROUND_IMAGES.map((image, index) => (
        <img
          key={image.src}
          src={image.src}
          alt={image.alt}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
            index === currentBackgroundIndex ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}

      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/84 to-slate-950/55" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(0,122,201,0.22),transparent_34%),radial-gradient(circle_at_85%_25%,rgba(242,113,49,0.14),transparent_30%)]" />

      <div className="relative z-10 mx-auto grid min-h-[calc(100vh-78px)] max-w-7xl items-center gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.22em] text-sky-200 backdrop-blur-xl">
            <span className="h-2 w-2 rounded-full bg-orange-400" />
            {content.eyebrow}
          </div>

          {firstName && mode !== "visitor" && (
            <p className="mt-5 text-sm font-semibold uppercase tracking-[0.18em] text-white/60">
              Bonjour {firstName}
            </p>
          )}

          <h1 className="mt-4 font-heading text-[2.55rem] font-bold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl">
            {content.title}
          </h1>

          <p className="mt-6 max-w-2xl text-base font-medium leading-8 text-slate-200 sm:text-lg">
            {content.description}
          </p>

          <div className="mt-8 flex flex-col gap-3 xs:flex-row">
            <button
              type="button"
              onClick={() => navigate(content.primaryPath)}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 active:translate-y-0 font-ui"
            >
              {content.primaryLabel}
              <FaChevronRight className="text-xs" />
            </button>

            <button
              type="button"
              onClick={() => navigate(content.secondaryPath)}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-6 py-3 text-sm font-bold text-white backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/15 active:translate-y-0 font-ui"
            >
              <FaPlayCircle />
              {content.secondaryLabel}
            </button>
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-md lg:max-w-lg">
          <div
            className={`absolute -inset-6 rounded-[2.5rem] ${featureAudience.glowClass} blur-3xl`}
          />

          <div className="relative rounded-[2.2rem] border border-white/20 bg-white/15 p-3 shadow-2xl shadow-black/40 backdrop-blur-2xl">
            <div
              className={`overflow-hidden rounded-[1.8rem] bg-gradient-to-br ${featureAudience.cardGradient} shadow-2xl`}
            >
              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/75">
                      Fonctions principales
                    </p>

                    <h2 className="mt-3 font-heading text-3xl font-bold leading-tight text-white sm:text-4xl">
                      {featureAudience.title}
                    </h2>

                    <p className="mt-4 text-sm font-semibold leading-6 text-white/85">
                      {featureAudience.description}
                    </p>
                  </div>

                  <div
                    className={`hidden rounded-2xl border p-4 shadow-inner sm:flex ${featureAudience.iconClass}`}
                  >
                    <AudienceIcon className="text-3xl" />
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-2 rounded-2xl border border-white/20 bg-white/20 p-1">
                  {Object.entries(FEATURE_AUDIENCES).map(([key, audience]) => {
                    const isActive = selectedAudience === key;

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedAudience(key)}
                        className={`min-h-11 rounded-xl px-4 py-2 text-sm font-bold font-ui transition ${
                          isActive
                            ? "bg-white text-slate-950 shadow-lg"
                            : "text-white/80 hover:bg-white/15 hover:text-white"
                        }`}
                      >
                        {audience.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2 border-t border-white/20 bg-slate-950/12 p-4 sm:p-5">
                {featureAudience.features.map((feature) => {
                  const Icon = feature.icon;

                  return (
                    <div
                      key={feature.title}
                      className="flex items-start gap-3 rounded-2xl border border-white/20 bg-white/18 p-3 backdrop-blur-xl transition hover:bg-white/25 font-ui"
                    >
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/20 text-white">
                        <Icon className="text-lg" />
                      </div>

                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white">
                          {feature.title}
                        </p>

                        <p className="mt-1 text-xs font-base leading-5 text-white/78">
                          {feature.text}
                        </p>
                      </div>
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={handleAudienceCta}
                  className="mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-slate-950 shadow-lg transition hover:-translate-y-0.5 hover:bg-white/90 active:translate-y-0 font-ui"
                >
                  {audienceCtaLabel}
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={scrollToNextSection}
        className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2 text-white/80 transition hover:text-white"
      >
        <span className="flex h-11 w-11 animate-bounce items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur-xl">
          <FaArrowDown />
        </span>

        <span className="text-xs font-bold uppercase tracking-[0.18em]">
          Découvrir
        </span>
      </button>
    </header>
  );
}

export default HomeHeroSection;